import { useAsync } from 'react-async';
import { useTranslation } from 'react-i18next';
import { useError, useLanguage } from 'hooks';
import { getExperience } from 'api/experiences';
import { isEqual } from 'lodash';
import {
  KoobButton,
  KoobModal,
  KoobPlaceholder,
  sanitizeRichText,
} from '@koob/ui';
import { BookingStatusBadge } from '@koob/booking';
import { formatCurrency, KoobStateEnum } from 'utils';
import { destroy } from 'api';
import ExperienceExtraComposition from 'containers/Experiences/Partials/ExperienceExtraComposition';
import { ExperienceDynamicCancelConditions } from '@koob/booking';
import { AdjustmentKindEnum } from '@koob/enums';
import { formatDate, yearsSince } from '@koob/dates';

const cancelExtraBooked = ({ extraCompositionId }) => {
  return destroy(`/api/v1/experiences/extras/${extraCompositionId}`);
};

const amountColor = color => {
  if (color === 'orange') return 'text-orange-500';
  if (color === 'green') return 'text-emerald-600';
  if (color === 'red') return 'text-rose-600';
  return 'text-slate-800';
};

const PricingRow = ({ label, amount, currency, bold, color }) => (
  <div
    className={`flex justify-between items-center py-1 ${
      bold ? 'font-semibold' : ''
    }`}
  >
    <span className={`text-sm ${bold ? 'text-slate-900' : 'text-slate-600'}`}>
      {label}
    </span>
    <span
      className={`text-sm ${amountColor(color)} ${bold ? 'font-semibold' : ''}`}
    >
      {formatCurrency({ amount, currency })}
    </span>
  </div>
);

const BookingExtrasModal = ({
  open,
  setOpen,
  extra,
  currency,
  reload,
  extras = [],
  bookingId,
}) => {
  const { t } = useTranslation('experiences');
  const { lang } = useLanguage();
  const { sendErrorSnack } = useError();
  const {
    data,
    reload: reloadExperience,
    isLoading,
  } = useAsync({
    promiseFn: getExperience,
    experienceId: extra?.id,
    locale: lang,
    // KD-3386: this extra is already booked, so it must stay readable even once
    // the DMC stops distributing it. The API grants that only against the
    // booking carrying it.
    bookingId,
    watchFn: (props, prevProps) => {
      return !isEqual(props.experienceId, prevProps.experienceId);
    },
  });

  const experience = data?.data;
  const hasPromo = extra?.price !== extra?.priceWithoutPromo;
  const pricing = extra?.pricingSummary;
  const hasAdjustments =
    !!pricing &&
    (pricing.adjustmentAmount !== 0 ||
      pricing.baseTotal !== pricing.totalPrice);

  const cancelExtra = async () => {
    try {
      await cancelExtraBooked({
        extraCompositionId: extra?.extraCompositionId,
      });
      reloadExperience();
      reload();
    } catch (error) {
      sendErrorSnack(error);
    }
    setOpen();
  };

  let assignedTravelers = extra?.travelers || [];

  if (extra?.isIncluded) {
    assignedTravelers = [];
  } else if (extra?.parentId) {
    assignedTravelers =
      extras
        .filter(
          e =>
            Number(e.id) === Number(extra.parentId) &&
            e.dayIndex === extra.dayIndex,
        )
        .flatMap(e => e.travelers ?? []) || [];
  }

  return (
    <KoobModal widthClass="sm:max-w-2xl" open={open} setOpen={() => setOpen()}>
      <div className="bg-white rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="p-5 space-y-3">
            <div className="flex gap-4">
              <KoobPlaceholder className="w-16 h-16 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <KoobPlaceholder className="w-48 h-5 rounded" />
                <KoobPlaceholder className="w-28 h-4 rounded" />
              </div>
            </div>
            <KoobPlaceholder className="w-full h-12 rounded-lg" />
            <KoobPlaceholder className="w-full h-24 rounded-lg" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-start gap-4 p-5 pb-4">
              <img
                src={
                  experience?.pictures?.[0]?.imageUrl ??
                  '/img/hotel-image-placeholder.png'
                }
                className="w-16 h-16 rounded-lg object-cover flex-shrink-0 bg-gray-100"
                alt="experience"
                onError={e =>
                  (e.target.src = '/img/hotel-image-placeholder.png')
                }
              />
              <div className="flex-1 min-w-0">
                <p className="text-base font-semibold text-slate-900 leading-snug">
                  {extra?.name}
                </p>
                <a
                  className="text-xs text-slate-400 underline hover:text-slate-600"
                  href={`/experiences/${experience?.id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('extras.learnMore')}
                </a>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <BookingStatusBadge status={extra?.state} />
                <button
                  onClick={() => setOpen()}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <i className="fa-regular fa-xmark text-lg" />
                </button>
              </div>
            </div>

            <div className="px-5 pb-5 space-y-3">
              {/* Extra name */}
              <div className="border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700">
                {extra?.name}
              </div>

              {/* Pax + price */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
                <div className="flex items-center justify-between">
                  <ExperienceExtraComposition
                    adults={extra?.adultPax}
                    childrenCount={extra?.childrenBirthdates?.length}
                  />
                  <div className="text-right">
                    {hasPromo && (
                      <div className="text-xs line-through text-slate-400">
                        {formatCurrency({
                          amount: extra?.priceWithoutPromo,
                          currency,
                        })}
                      </div>
                    )}
                    {extra?.isIncluded ? (
                      <span className="text-sm text-slate-500">
                        {t('extras.included')}
                      </span>
                    ) : (
                      extra?.price && (
                        <span className="text-sm font-semibold text-orange-500">
                          {formatCurrency({
                            amount: hasAdjustments
                              ? pricing.totalPrice
                              : extra?.price,
                            currency,
                          })}
                        </span>
                      )
                    )}
                  </div>
                </div>
                {assignedTravelers.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200/60 space-y-1.5">
                    <p className="text-xs font-medium text-slate-400 bold-sm">
                      {t('folders:guests:title')}:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {assignedTravelers.map((traveler, index) => {
                        const firstName =
                          traveler?.firstname || traveler?.firstName || '';
                        const lastName =
                          traveler?.lastname || traveler?.lastName || '';
                        const birthdate = traveler?.birthdate
                          ? ` (${formatDate(traveler.birthdate, 'DD/MM/YYYY')})`
                          : '';

                        const isTravelerChild =
                          !!traveler?.birthdate &&
                          yearsSince(traveler.birthdate) < 18;

                        const travelerIcon = (
                          <i
                            className={`fa-regular ${
                              isTravelerChild ? 'fa-child' : 'fa-user'
                            } text-xs`}
                          />
                        );

                        return (
                          <div
                            key={traveler.id || index}
                            className="inline-flex items-center bg-white border border-slate-200 rounded-md px-2 py-1 text-sm text-slate-700 gap-1.5"
                          >
                            {travelerIcon}
                            <span>
                              {firstName} {lastName}
                              {birthdate}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Pricing summary */}
              {hasAdjustments && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                    <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                      {t('extras.priceBreakdown')}
                    </span>
                  </div>
                  <div className="px-4 py-3 space-y-0.5">
                    <PricingRow
                      label={t('extras.basePrice')}
                      amount={pricing.baseTotal}
                      currency={currency}
                    />
                    <PricingRow
                      label={t('extras.exceptionalAdjustment', {
                        kind:
                          pricing.adjustmentAmount > 0
                            ? AdjustmentKindEnum.SUPPLEMENT
                            : AdjustmentKindEnum.DISCOUNT,
                      })}
                      amount={pricing.adjustmentAmount}
                      currency={currency}
                      color={pricing.adjustmentAmount >= 0 ? 'green' : 'red'}
                    />
                    <div className="border-t border-slate-100 mt-2 pt-2">
                      <PricingRow
                        label={t('extras.total')}
                        amount={pricing.totalPrice}
                        currency={currency}
                        bold
                        color="orange"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Cancel conditions */}
              {extra?.cancelConditions && (
                <div className="border border-slate-200 rounded-lg px-4 py-3">
                  <p className="text-sm font-semibold text-slate-800 mb-2">
                    {t('contractingConditions')}
                  </p>
                  <ExperienceDynamicCancelConditions
                    currency={currency}
                    translationKey="booking"
                    cancelConditions={extra?.cancelConditions?.details}
                  />
                  {extra?.cancelConditions?.text && (
                    <div
                      className="ql-editor text-sm text-slate-600 mt-2 p-0 max-h-[300px] overflow-y-auto"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeRichText(extra.cancelConditions.text),
                      }}
                    />
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-1">
                {!extra?.isIncluded && (
                  <KoobButton
                    variant="danger"
                    disabled={extra?.state === KoobStateEnum.CANCELED}
                    onClick={cancelExtra}
                  >
                    {t('misc:cancel')}
                  </KoobButton>
                )}
                <KoobButton variant="primary" onClick={() => setOpen()}>
                  {t('misc:close')}
                </KoobButton>
              </div>
            </div>
          </>
        )}
      </div>
    </KoobModal>
  );
};

export default BookingExtrasModal;
